import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useState, useCallback } from "react";
import { db } from "~/db/db";
import { getHierarquiasAprovacao, getSimulacoes, sendCompartilharSimulacao } from "~services/SimulatorsServices";
import { useParams, useNavigate } from "react-router-dom";
import useProduto from "./useProduto";
import useGrupoProduto from "./useGrupoProduto";
import { isBefore } from "date-fns";
import useFilial from "./useFilial";
import usePrazoPagamento from "./usePrazoPagamento";
import useVendedor from "~/fetchs/useVendedor";

const matchesSearch = (simulacao, searchText) => {
  if (!searchText) return true;

  const searchLower = searchText.toLowerCase();
  const fields = [simulacao.nome, simulacao.documento, simulacao.segmento,
                  simulacao.grupoCentralizador, simulacao.codigo];

  return fields.some(field => field?.toLowerCase().includes(searchLower));
};

const getStartDateByPeriod = (period) => {
  if (!period) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(today);

  switch (period) {
    case "last30": startDate.setDate(today.getDate() - 30); break;
    case "last15": startDate.setDate(today.getDate() - 15); break;
    case "last7": startDate.setDate(today.getDate() - 7); break;
    case "last3": startDate.setDate(today.getDate() - 3); break;
    default: return null;
  }

  return startDate;
};

const applyClientFilters = (simulacao, filtersCliente) => {
  if (!simulacao) return false;

  const { grupoProduto, produto, periodo } = filtersCliente;

  if (grupoProduto && simulacao.grupoMaterialId !== grupoProduto) {
    return false;
  }


  if (produto && simulacao.produtoId !== produto) {
    return false;
  }

  if (periodo) {
    const startDate = getStartDateByPeriod(periodo);
    if (startDate && !isBefore(startDate, new Date(simulacao.createdAt))) {
      return false;
    }
  }

  return true;
};

const sortSimulations = (simulations, order) => {
  if (!order || !simulations) return simulations;

  return [...simulations].sort((a, b) => {
    const dataA = new Date(a.createdAt);
    const dataB = new Date(b.createdAt);

    if (order === "recente") {
      return dataB.getTime() - dataA.getTime();
    } else if (order === "antigo") {
      return dataA.getTime() - dataB.getTime();
    }

    return 0;
  });
};

const useSimulacoes = ({ type = "all", groupClient = false, skip = 0, take = 10 }) => {
  const navigate = useNavigate();
  const { parameter: idCliente } = useParams();
  const [enabledSimulacoes, setEnabledSimulacoes] = useState(true);
  const { clientesSelecionados } = useVendedor();
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    busca: "",
    orderBy: "",
  });

  const [filtersCliente, setFiltersCliente] = useState({
    grupoProduto: 0,
    produto: 0,
    tipoSimulacao: "",
    periodo: "",
    ordem: "",
    orderBy: "recente",
  });

  const { gruposProduto } = useGrupoProduto({ formikRef: null, name: "grupoProdutoId" });
  const { prazoPagamentos } = usePrazoPagamento({ formikRef: null, name: "prazoPagamentoId" });

  const [simulacoesSemFiltro, setSimulacoesSemFiltro] = useState([]);
  const [simulacoesDB, setSimulacoesDB] = useState([]);
  const [simulacoesTotal, setSimulacoesTotal] = useState(0);

  const fetchSimulacoesSemFiltro = useCallback(async () => {
    let query = db.simulacoes;

    if (type === "cliente") {
      query = query.where("clienteId").equals(parseInt(idCliente));
    }

    if (clientesSelecionados) {
      query = query.filter(x => clientesSelecionados.includes(x.clienteId));
    }

    const results = [];
    await query.each((item) => results.push(item));
    setSimulacoesSemFiltro(results);
  }, [type, idCliente, clientesSelecionados]);

  const fetchGroupedSimulations = useCallback(async () => {
    let keys = db.simulacoes.orderBy("clienteId");
    if (clientesSelecionados) {
      keys = keys.filter(x => clientesSelecionados.includes(x.clienteId));
    }
    keys = await keys.uniqueKeys();

    let groupedSimulations = await Promise.all(
      keys.map(async (clienteId) => {
        const simulacao = await db.simulacoes.where("clienteId").equals(clienteId).sortBy("createdAt");
        return simulacao[simulacao.length - 1];
      })
    );

    if (filters.busca) {
      groupedSimulations = groupedSimulations.filter(simulacao =>
        matchesSearch(simulacao, filters.busca)
      );
    }

    return groupedSimulations;
  }, [filters.busca, clientesSelecionados]);

  const fetchUngroupedSimulations = useCallback(async () => {
    let query = db.simulacoes;

    if (type === "cliente") {
      query = query.where("clienteId").equals(parseInt(idCliente));
    }

    const hasClientFilters = filtersCliente.grupoProduto || filtersCliente.produto ||
                            filtersCliente.orderBy || filtersCliente.periodo ||
                            filtersCliente.tipoSimulacao;

    if (hasClientFilters) {
      if (clientesSelecionados) {
        query = query.filter(x => clientesSelecionados.includes(x.clienteId));
      }

      const results = [];
      await query.each((simulacao) => {
        if (applyClientFilters(simulacao, filtersCliente)) {
          results.push(simulacao);
        }
      });
      return results;
    } else {
      const results = [];
      await query.each((simulacao) => {
        if (filters.busca) {
          if (matchesSearch(simulacao, filters.busca)) {
            results.push(simulacao);
          }
        } else {
          results.push(simulacao);
        }
      });
      return results;
    }
  }, [type, idCliente, filtersCliente, filters.busca, clientesSelecionados]);


  useEffect(() => {
    fetchSimulacoesSemFiltro();
  }, [fetchSimulacoesSemFiltro]);

  useEffect(() => {
    const fetchSimulacoes = async () => {
      const results = groupClient
        ? await fetchGroupedSimulations()
        : await fetchUngroupedSimulations();

      setSimulacoesDB(results);
    };

    fetchSimulacoes();
  }, [groupClient, fetchGroupedSimulations, fetchUngroupedSimulations]);

  const simulacoes = useMemo(() => {
    if (!simulacoesDB) return [];

    let result = arrumaSimulacoes(simulacoesDB, gruposProduto, prazoPagamentos);

    const order = filters.periodo || filtersCliente.orderBy;
    if (order) {
      result = sortSimulations(result, order);
    }

    const startIndex = skip;
    const endIndex = skip + take;
    result = result.slice(startIndex, endIndex);

    if (result) setLoading(false);
    return result;
  }, [simulacoesDB, filters.periodo, filtersCliente.orderBy, gruposProduto, skip, take]);

  useEffect(() => {
    setSimulacoesTotal(simulacoesDB?.length || 0);
  }, [simulacoesDB]);

  return {
    simulacoesSemFiltro,
    loading,
    setLoading,
    simulacoes,
    filters,
    setFilters,
    filtersCliente,
    setFiltersCliente,
    total: simulacoesTotal,
    setEnabledSimulacoes,
  };
};

export default useSimulacoes;

export function arrumaSimulacoes(simulacoes, gruposProduto, prazoPagamentos) {
  const simulacoesObj = {};
  const gruposMateriaisObj = {};
  const produtosObj = {};

  simulacoes.forEach((obj) => {
    const simulacaoId = obj.simulacaoId;

    if (!simulacoesObj[simulacaoId]) {
      simulacoesObj[simulacaoId] = {
        simulacaoId: obj.simulacaoId,
        clienteId: obj.clienteId,
        fracionamento: obj.fracionamento,
        descargaManual: obj.descargaManual,
        documento: obj.documento,
        segmento: obj.segmento,
        grupoCentralizador: obj.grupoCentralizador,
        codigo: obj.codigo,
        simulador: obj.simulador,
        nome: obj.nome,
        createdAt: obj.createdAt,
        gruposMateriais: {},
      };
    }

    const grupoMaterialId = obj.grupoMaterialId;
    if (!gruposMateriaisObj[simulacaoId]) {
      gruposMateriaisObj[simulacaoId] = {};
    }

    if (!gruposMateriaisObj[simulacaoId][grupoMaterialId]) {
      gruposMateriaisObj[simulacaoId][grupoMaterialId] = {
        grupoMaterial: (gruposProduto || []).find((opt) => opt.id === grupoMaterialId)?.nome,
        grupoMaterialId,
        id: obj?.simulacaoGrupoId,
        produtos: [],
      };
    }


    const produtoKey = `${obj.produtoId}-${obj.filialId}`;
    if (!produtosObj[produtoKey]) {
      produtosObj[produtoKey] = {
        produto: obj.descricaoProduto,
        filial: obj.descricaoFilial,
        filialId: obj.filialId,
        produtoId: obj.produtoId,
        grupoMaterialId: obj.grupoMaterialId,
        quantidade: obj.quantidade,
        tipoCondicaoId: obj.tipoCondicaoId,
        condicao: obj.condicao,
        enviaPrazoCif: obj.enviaPrazoCif,
        enviaPrazoFob: obj.enviaPrazoFob,
        prazoPagamentoId: obj.prazoPagamentoCifId ?? obj.prazoPagamentoFobId,
        precoFinal: {
          fob: obj.precoFinalFob,
          cif: obj.precoFinalCif,
        },
        precoRegra: {
          fob: obj.precoRegraFob,
          cif: obj.precoRegraCif,
        },
        desconto: {
          fob: obj.descontoFob,
          cif: obj.descontoCif,
        },
        id: obj.id,
        enviadoAprovacao: obj.enviadoAprovacao,
        codigo: obj.codigoProduto,
        codigoFilial: obj.codigoFilial,
        grupoMaterialReplicacaoId: obj.grupoMaterialReplicacaoId,
        familiaMaterialReplicacaoId: obj.familiaMaterialReplicacaoId,
        produtoReplicacaoId: obj.produtoReplicacaoId,
        grupoCentralizadorReplicacaoId: obj.grupoCentralizadorReplicacaoId,
        beneficioFis: {
          cif: obj.beneficioFiscalCif,
          fob: obj.beneficioFiscalFob,
        },
      };
    }

    gruposMateriaisObj[simulacaoId][grupoMaterialId].produtos.push(produtosObj[produtoKey]);
  });

  const result = Object.values(simulacoesObj).map((simulacao) => {
    const simulacaoId = simulacao.simulacaoId;
    simulacao.gruposMateriais = Object.values(gruposMateriaisObj[simulacaoId] || {}).map((grupoMaterial) => {
      return {
        ...grupoMaterial,
        produtos: grupoMaterial.produtos,
      };
    });
    return simulacao;
  });

  return result;
}

export const useGetSimulacoes = (enable, skip, take, dataAtualizacao) => {
  const simulacoes = useQuery(["simulacoes"], () => getSimulacoes(skip, take, dataAtualizacao), {
    enabled: enable,
  });

  const queryClient = useQueryClient();

  const clearCache = () => {
    queryClient.removeQueries(["simulacoes"]);
  };

  return { ...simulacoes, clearCache };
};

export const useGetHierarquiasAprovacao = (enable, skip, take, dataAtualizacao) => {
  const hierarquias = useQuery(["hierarquias-aprovacao"], () => getHierarquiasAprovacao(skip, take, dataAtualizacao), {
    enabled: enable,
  });

  const queryClient = useQueryClient();

  const clearCache = () => {
    queryClient.removeQueries(["hierarquias-aprovacao"]);
  };
  return { ...hierarquias, clearCache };
}

export const useCompartilharSimulacao = (enable, id, email, attachment) => {
  const compartilhar = useQuery(["compartilhar"], () => sendCompartilharSimulacao(id, email, attachment), {
    enabled: enable,
  });

  const queryClient = useQueryClient();

  const clearCache = () => {
    queryClient.removeQueries(["compartilhar"]);
  };

  return { ...compartilhar, clearCache };
};